import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { NumericInput } from "@/components/ui/numeric-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Tag, AlertCircle, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";

export interface Pro014StepThreeData {
  takeQuantity: number;
  payQuantity: number;
  discountValue: number;
  discountType: number;
  paymentMethod: number;
  baseValue: number;
}

interface StepThreeProps {
  data: Pro014StepThreeData;
  updateData: (data: Partial<Pro014StepThreeData>) => void;
  nextStep: () => void;
  prevStep: () => void;
}

const discountTypes = [
  { value: 0, label: "Desconto integral na 2ª peça (mesmo item)", group: "MESMO ITEM" },
  { value: 1, label: "Percentual na 2ª peça (mesmo item)", group: "MESMO ITEM" },
  { value: 2, label: "Valor fixo de desconto (mesmo item)", group: "MESMO ITEM" },
  { value: 3, label: "Desconto integral (itens da lista)", group: "ITENS DA LISTA" },
  { value: 4, label: "Percentual (itens da lista)", group: "ITENS DA LISTA" },
  { value: 5, label: "Valor fixo de desconto (itens da lista)", group: "ITENS DA LISTA" },
  { value: 7, label: "Brinde - escolher item da lista", group: "BRINDE" },
  { value: 8, label: "Brinde - mesmo item", group: "BRINDE" },
];

// STORY-NEW-024: paymentMethods comentado (campo removido do frontend)
// const paymentMethods = [
//   { value: 0, label: "Todas as formas" },
//   { value: 1, label: "Cartão da loja" },
//   { value: 2, label: "Dinheiro/Débito" },
// ];

export default function StepThree({ data, updateData, nextStep, prevStep }: StepThreeProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Tipos que exigem discountValue > 0: 1 (Percentual), 2 (Valor Fixo), 4 (Percentual Lista), 5 (Valor Fixo Lista)
  // Tipos que permitem discountValue = 0: 0, 3, 7, 8 (desconto integral e brinde)
  const typesRequiringDiscountValue = [1, 2, 4, 5];

  const isFormValid = useMemo(() => {
    const hasValidDiscountType = [0, 1, 2, 3, 4, 5, 7, 8].includes(data.discountType);
    const hasValidTakeQuantity = data.takeQuantity >= 1;
    const hasValidPayQuantity = data.payQuantity < data.takeQuantity;
    // Verifica se o tipo de desconto exige valor > 0
    const requiresDiscountValue = [1, 2, 4, 5].includes(data.discountType);
    const hasValidDiscountValue = requiresDiscountValue
      ? data.discountValue > 0
      : data.discountValue >= 0;
    const hasValidBaseValue = data.baseValue >= 0;
    return hasValidDiscountType && hasValidTakeQuantity && hasValidPayQuantity && hasValidDiscountValue && hasValidBaseValue;
  }, [data.discountType, data.takeQuantity, data.payQuantity, data.discountValue, data.baseValue]);

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Pague < Leve
    if (data.payQuantity > 0 && data.payQuantity >= data.takeQuantity) {
      newErrors.payQuantity = "Quantidade 'Pague' deve ser menor que 'Leve'";
    }

    // Valor/Percentual: validação condicional baseada no tipo de desconto
    const requiresDiscountValue = typesRequiringDiscountValue.includes(data.discountType);
    if (requiresDiscountValue && data.discountValue <= 0) {
      newErrors.discountValue = "Para o tipo de desconto selecionado, o valor/percentual deve ser maior que 0";
    } else if (data.discountValue < 0) {
      newErrors.discountValue = "Valor/Percentual não pode ser negativo";
    }

    // Tipo válido (0-8, exceto 6)
    if (![0, 1, 2, 3, 4, 5, 7, 8].includes(data.discountType)) {
      newErrors.discountType = "Tipo de desconto inválido";
    }

    if (data.takeQuantity < 1) {
      newErrors.takeQuantity = "Quantidade 'Leve' deve ser no mínimo 1";
    }

    if (data.baseValue < 0) {
      newErrors.baseValue = "Valor base não pode ser negativo";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      // STORY-NEW-024: Garantir que paymentMethod seja sempre 0
      updateData({ paymentMethod: 0 });
      nextStep();
    } else {
      toast({
        variant: "destructive",
        title: "Erro de validação",
        description: "Verifique os campos obrigatórios antes de continuar.",
      });
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div className="bg-card rounded-lg border p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">
              Configuração de Desconto *
            </h3>
            <p className="text-sm text-muted-foreground">
              Defina as regras de desconto da campanha
            </p>
          </div>
        </div>

        {/* Tipo de Desconto */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="discountType" className="text-sm font-medium">
              Tipo de Desconto *
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div className="space-y-2 text-xs">
                    <div>
                      <p className="font-semibold">MESMO ITEM (0-2):</p>
                      <p>Cliente precisa levar MÚLTIPLAS unidades do MESMO produto</p>
                      <p className="text-muted-foreground">Ex: Leve 2 camisetas azuis (101.616)</p>
                    </div>
                    <div>
                      <p className="font-semibold">ITENS DA LISTA (3-5, 7):</p>
                      <p>Cliente pode combinar QUALQUER produto da lista</p>
                      <p className="text-muted-foreground">Ex: 1 camiseta azul + 1 vermelha</p>
                    </div>
                    <div>
                      <p className="font-semibold">BRINDE (7-8):</p>
                      <p>Cliente ganha produto grátis ao atingir condição</p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select
            value={data.discountType?.toString()}
            onValueChange={(value) => {
              updateData({ discountType: parseInt(value) });
              setErrors({ ...errors, discountType: "" });
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Selecione o tipo de desconto" />
            </SelectTrigger>
            <SelectContent>
              {discountTypes.map((type, index, array) => {
                const prevGroup = index > 0 ? array[index - 1].group : null;
                const showGroupHeader = prevGroup !== type.group;

                return (
                  <div key={type.value}>
                    {showGroupHeader && (
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted">
                        {type.group}
                      </div>
                    )}
                    <SelectItem value={type.value.toString()}>{type.label}</SelectItem>
                  </div>
                );
              })}
            </SelectContent>
          </Select>
          {errors.discountType && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.discountType}
            </p>
          )}
        </div>

        {/* Leve e Pague */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="takeQuantity" className="text-sm font-medium">
              Leve *
            </Label>
            <NumericInput
              id="takeQuantity"
              value={data.takeQuantity}
              onChange={(val) => {
                updateData({ takeQuantity: val });
                setErrors({ ...errors, takeQuantity: "", payQuantity: "" });
              }}
              min={1}
              max={99}
              maxLength={2}
              placeholder="Ex: 2"
              hasError={!!errors.takeQuantity}
            />
            {errors.takeQuantity && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.takeQuantity}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="payQuantity" className="text-sm font-medium">
              Pague
            </Label>
            <NumericInput
              id="payQuantity"
              value={data.payQuantity}
              onChange={(val) => {
                updateData({ payQuantity: val });
                setErrors({ ...errors, payQuantity: "" });
              }}
              min={0}
              max={99}
              maxLength={2}
              placeholder="Ex: 1"
              hasError={!!errors.payQuantity}
            />
            {errors.payQuantity && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.payQuantity}
              </p>
            )}
          </div>
        </div>

        {/* Valor/Percentual */}
        <div className="space-y-2">
          <Label htmlFor="discountValue" className="text-sm font-medium">
            Valor/Percentual *
          </Label>
          <NumericInput
            id="discountValue"
            value={data.discountValue}
            onChange={(val) => {
              updateData({ discountValue: val });
              setErrors({ ...errors, discountValue: "" });
            }}
            min={0}
            max={9999.99}
            maxLength={7}
            step={0.01}
            numberType="decimal"
            placeholder="0"
            hasError={!!errors.discountValue}
          />
          <p className="text-xs text-muted-foreground">
            Para tipo percentual, insira o valor sem o símbolo % (ex: 50 para 50%)
          </p>
          {errors.discountValue && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.discountValue}
            </p>
          )}
        </div>

        {/*
          CAMPO REMOVIDO: Forma de Pagamento
          Data: 2025-11-19
          Motivo: Simplificação do processo - sempre usar "Todas as Formas" (valor 0)
          Story: STORY-NEW-024

          Para reverter: Descomentar este bloco e verificar backend DTO
        */}
        {/* <div className="space-y-2">
          <Label htmlFor="paymentMethod" className="text-sm font-medium" style={{ color: '#2E0D1E' }}>
            Forma de Pagamento *
          </Label>
          <Select
            value={data.paymentMethod?.toString()}
            onValueChange={(value) => updateData({ paymentMethod: parseInt(value) })}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Selecione a forma de pagamento" />
            </SelectTrigger>
            <SelectContent>
              {paymentMethods.map((method) => (
                <SelectItem key={method.value} value={method.value.toString()}>
                  {method.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div> */}

        {/* Valor Base Mínimo (Opcional) */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="baseValue" className="text-sm font-medium">
              Valor Base Mínimo (Opcional)
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs max-w-xs">
                    Valor mínimo de compra para ativar a promoção.
                    Ex: R$ 100,00 → Cliente precisa gastar mínimo R$ 100
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <NumericInput
            id="baseValue"
            value={data.baseValue}
            onChange={(val) => {
              updateData({ baseValue: val });
              setErrors({ ...errors, baseValue: "" });
            }}
            min={0}
            max={99999.99}
            maxLength={8}
            step={0.01}
            numberType="decimal"
            placeholder="0"
            hasError={!!errors.baseValue}
            className="h-9"
          />
          {errors.baseValue && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.baseValue}
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          onClick={prevStep}
          className="w-full md:w-auto"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isFormValid}
          className="w-full md:w-auto"
        >
          Continuar
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
